import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import HyperLiquidClient from '../../clients/HyperLiquidClient'
import { CandleProbe } from '../../interfaces/hyperliquid'
import { logger } from '../../logger'
import { formatMoney, formatNumber } from '../../utils/formatNumber'
import useBalances from '../useBalances'
import useConfig from '../useConfig'
import { usePerpAssetMeta } from './usePerpAssetMeta'

export interface PerpChartRange {
  title: string
  interval: string
  rangeSeconds: number
}

export const PERP_CHART_RANGES: PerpChartRange[] = [
  {
    title: '1H',
    interval: '1m',
    rangeSeconds: 60 * 60,
  },
  {
    title: '1D',
    interval: '30m',
    rangeSeconds: 24 * 60 * 60,
  },
  {
    title: '1W',
    interval: '4h',
    rangeSeconds: 7 * 24 * 60 * 60,
  },
  {
    title: '1M',
    interval: '12h',
    rangeSeconds: 30 * 24 * 60 * 60,
  },
  {
    title: 'YTD',
    interval: '1d',
    rangeSeconds: 365 * 24 * 60 * 60,
  },
]

export interface Candle {
  time: number
  open: number
  high: number
  low: number
  close: number
}

interface PriceChange {
  value: number
  percentage: number
}

const mapCandle = ({ o, c, h, l, t }: CandleProbe): Candle => ({
  open: parseFloat(o),
  close: parseFloat(c),
  high: parseFloat(h),
  low: parseFloat(l),
  time: t / 1000,
})

const getPriceChange = (latestClose?: number, previousClose?: number): PriceChange | undefined => {
  if (!latestClose || !previousClose) return

  if (previousClose === 0) return

  const absoluteChange = latestClose - previousClose
  return { value: absoluteChange, percentage: (absoluteChange / previousClose) * 100 }
}

const usePerpsDetails = (hyperliquidTokenId: number, interval: PerpChartRange) => {
  const hyperliquidClient = useMemo(() => new HyperLiquidClient(), [])
  const [candles, setCandles] = useState<Candle[]>([])
  const [priceChange, setPriceChange] = useState<PriceChange>()
  const [price, setPrice] = useState<number>()
  const { perpsAccountBalance } = useBalances()
  const { meta } = usePerpAssetMeta(hyperliquidTokenId)
  const { t } = useTranslation()

  const { tokens, perpsTokens } = useConfig()
  const perpToken = perpsTokens.find((t) => t.id === hyperliquidTokenId)
  const token = tokens.find((t) => t.additionalData.hyperliquidId === hyperliquidTokenId.toString())

  useEffect(() => {
    if (!perpToken) return

    hyperliquidClient
      .getCandleSnapshot({
        coin: perpToken?.name,
        interval: interval.interval,
        endTime: Date.now(),
        startTime: Date.now() - interval.rangeSeconds * 1000,
      })
      .then((res) => {
        const mapped = res.map(mapCandle)
        setCandles(mapped)

        if (mapped.length > 2) {
          setPriceChange(getPriceChange(mapped.at(-1)?.close, mapped[0].close))
          setPrice(mapped.at(-1)?.close)
        }
      })
      .catch(logger.error)
  }, [perpToken, interval])

  const currentPosition = useMemo(() => {
    return perpsAccountBalance?.assetPositions.filter((p) => p.position.coin === perpToken?.name)[0]
  }, [perpsAccountBalance, perpToken])

  const positionDetails = useMemo(() => {
    if (!currentPosition) return {}

    const pnl = parseFloat(currentPosition.position.unrealizedPnl)
    const roi = parseFloat(currentPosition.position.returnOnEquity) * 100
    const size = parseFloat(currentPosition.position.szi)
    const margin = parseFloat(currentPosition.position.marginUsed)
    const entryPrice = parseFloat(currentPosition.position.entryPx)
    const liquidationPrice = parseFloat(currentPosition.position.liquidationPx || '0')

    const isLong = size > 0

    return {
      cards: [
        { title: t('perps.details.pnl'), value: formatMoney(pnl), negative: pnl < 0 },
        { title: t('perps.details.roi'), value: formatNumber(roi, '%'), negative: roi < 0 },
        { title: t('perps.details.size'), value: formatNumber(size, token?.symbol) },
        { title: t('perps.details.margin'), value: formatMoney(margin) },
      ],
      table: [
        {
          title: t('perps.details.direction'),
          value: `${isLong ? t('perps.type.long') : t('perps.type.short')} ${currentPosition.position.leverage.value}x`,
        },
        { title: t('perps.details.entryPrice'), value: formatMoney(entryPrice) },
        {
          title: t('perps.liquidationPrice'),
          value: formatMoney(liquidationPrice),
        },
      ],
    }
  }, [currentPosition])

  const perpTokenInfo = useMemo(() => {
    if (!meta) return []

    return [
      { title: t('perps.details.dayVolume'), value: formatMoney(meta.dayVolume) },
      { title: t('perps.details.openInterest'), value: formatMoney(meta.openInterest) },
      { title: t('perps.details.funding'), value: formatNumber(meta.funding * 100, '%') },
    ]
  }, [meta])

  return { token, candles, price, priceChange, currentPosition, positionDetails, perpTokenInfo }
}

export default usePerpsDetails
